/**
 * Tax Identification Number (TIN) Validation Utilities
 * 
 * Provides validation for Tax Identification Numbers with country-specific patterns.
 * Currently supports Zimbabwe TIN format.
 */

export interface TinValidationResult {
  isValid: boolean;
  formatted?: string;
  error?: string;
  countryCode?: string;
}

export interface CountryTinConfig {
  countryCode: string;
  pattern: RegExp;
  formatter?: (tin: string) => string;
  example: string;
  description: string;
}

/**
 * Country-specific TIN validation configurations
 * 
 * Note: Zimbabwe TIN format research indicates:
 * - Format appears to be numeric
 * - Length varies but commonly 10-12 digits
 * - May include check digits
 * - ZIMRA (Zimbabwe Revenue Authority) issues these numbers
 * 
 * Since specific format documentation is limited, we use a flexible pattern
 * that accepts 8-15 digits, which covers most observed formats.
 */
export const TIN_CONFIGS: Record<string, CountryTinConfig> = {
  ZW: {
    countryCode: 'ZW',
    // Zimbabwe TIN: 8-15 digits (flexible pattern due to limited documentation)
    pattern: /^[0-9]{8,15}$/,
    formatter: (tin: string) => {
      // Remove all non-digits
      const cleaned = tin.replace(/\D/g, '');
      // Format as groups of 3-4 digits for readability
      if (cleaned.length >= 10) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{4})(.*)/, '$1-$2-$3-$4').replace(/-$/, '');
      }
      return cleaned;
    },
    example: '123-456-7890',
    description: 'Zimbabwe Tax Identification Number (8-15 digits)'
  },
  DEFAULT: {
    countryCode: 'DEFAULT',
    // Generic TIN pattern - alphanumeric, 6-20 characters
    pattern: /^[A-Z0-9]{6,20}$/i,
    example: 'TIN123456789',
    description: 'Tax Identification Number'
  }
};

/**
 * Normalize TIN by removing formatting characters
 */
export function normalizeTin(tin: string): string {
  return tin.replace(/[\s\-.]/g, '').toUpperCase();
}

/**
 * Validate TIN for a specific country
 */
export function validateTin(
  tin: string, 
  countryCode: string = 'ZW'
): TinValidationResult {
  if (!tin || typeof tin !== 'string') {
    return {
      isValid: false,
      error: 'TIN is required'
    };
  }

  const normalized = normalizeTin(tin.trim());
  
  if (normalized.length === 0) {
    return {
      isValid: false,
      error: 'TIN cannot be empty'
    };
  }

  // Get country configuration
  const config = TIN_CONFIGS[countryCode] || TIN_CONFIGS.DEFAULT;
  
  // Validate against pattern
  const isValid = config.pattern.test(normalized);
  
  if (!isValid) {
    return {
      isValid: false,
      error: `Invalid TIN format. Expected: ${config.description} (e.g., ${config.example})`,
      countryCode
    };
  }

  // Format the TIN if formatter is available
  const formatted = config.formatter ? config.formatter(normalized) : normalized;

  return {
    isValid: true,
    formatted,
    countryCode
  };
}

/**
 * Validate Zimbabwe TIN specifically
 */
export function validateZimbabweTin(tin: string): TinValidationResult {
  return validateTin(tin, 'ZW');
}

/**
 * Get TIN validation error message for UI display
 */
export function getTinValidationMessage(
  result: TinValidationResult,
  fieldName: string = 'TIN'
): string {
  if (result.isValid) return '';
  
  return result.error || `${fieldName} is invalid`;
}

/**
 * Check if TIN is valid for a country
 */
export function isValidTin(tin: string, countryCode: string = 'ZW'): boolean {
  const result = validateTin(tin, countryCode);
  return result.isValid;
}
